import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';

import { Grid } from '@/components/core/Grid';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useDynamicPageSize } from '@/hooks/useDynamicPageSize';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { columns } from './columns';
import { ContentDialog } from './components/ContentDialog';
import { Toolbar } from './components/Toolbar';
import {
    useApprovalEnhancedContentEntry,
    useDeleteEnhancedContentEntry,
    useEnhancedContentData,
    usePublishEnhancedContentEntry,
    useUpdateEnhancedContentEntry,
} from './enhanced-content.queries';
import { EnhancedContentDataType } from './enhanced-content.types';

export default function EnhancedContentPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { auth } = useAuth();

    const [textFilter, setTextFilter] = useState<string | undefined>(undefined);
    const [isContentDialogOpen, setIsDialogContentOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState<EnhancedContentDataType[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<EnhancedContentDataType | undefined>(undefined);
    const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const updateEnhancedEntryMutation = useUpdateEnhancedContentEntry({
        onError: (error) => {
            if (error.response.data) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
            }
        },
        onSuccess: () => {
            toast.success('ENHANCED_CONTENT.SAVED_SUCCESSFULLY');
        }
    });

    const pageSize = useDynamicPageSize();
    const {
        data: enhancedContentData,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch: refetchData,
    } = useEnhancedContentData({
        variables: {
            limit: pageSize,
            keywords: textFilter
        },

        select: (data) => data.pages.flatMap((page) => page.data),

        enabled: pageSize > 0,
    });

    const publishedItemsContentData = (enhancedContentData || []).filter(item => item?.needsPublishing === true && item?.approved === false);

    const deleteEnhancedContentMutation = useDeleteEnhancedContentEntry({
        onError: () => toast.error('ENHANCED_CONTENT.ERROR_OCCURRED'),
        onSuccess: () => {
            refetchData();
            setSelectedRows([]);
            toast.success('ENHANCED_CONTENT.DELETED_SUCCESSFULLY');
        },
    });

    const publishEnhancedEntryMutation = usePublishEnhancedContentEntry({
        onError: (error) => {
            if (error.response.data) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
            }
        },
        onSuccess: () => {
            toast.success('ENHANCED_CONTENT.PUBLISHED_SUCCESSFULLY');
        },
    });

    const approvalEnhancedEntryMutation = useApprovalEnhancedContentEntry({
        onError: (error) => {
            if (error.response.data) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
            }
        },
        onSuccess: (updatedEntity) => {
            refetchData();
            setSelectedRows([]);
            if (updatedEntity.approved) {
                toast.success('ENHANCED_CONTENT.SUCCESSFULLY_APPROVED');
                // after approved an item  --> publish
                updatedEntity.id && publishEnhancedEntryMutation.mutate({id: updatedEntity.id});
            } else {
                toast.success('ENHANCED_CONTENT.SUCCESSFULLY_UNAPPROVED');
            }
        },
    });

    const onSelectedRowsChange = useCallback(
        (rowSelection: EnhancedContentDataType[]) => setSelectedRows(rowSelection),
        []
    );

    const onInfiniteScroll = useCallback(() => hasNextPage && fetchNextPage(), [hasNextPage, fetchNextPage]);

    if (!auth) {
        return null;
    }

    const onDelete = (entities: EnhancedContentDataType[]) => {
        setSelectedRows(entities);
        setIsDeleteConfirmationDialogOpen(true);
        setShowConfirmationModal(true);
    };

    const onPublish = (entities: EnhancedContentDataType[]) =>{
        // if (canPublish) {
        //     entities.forEach(row => row.id && publishEnhancedEntryMutation.mutate({ id: row.id }));
        // }
        entities.forEach((row) =>{
            //  Set row.needsPublishing to true so the reviewer can see the published item
            row.needsPublishing= true;
            updateEnhancedEntryMutation.mutate({entry: row});
        });
    };

    const onEdit = (entity: EnhancedContentDataType) => {
        setSelectedEntity(entity);
        setIsDialogContentOpen(true);
    };

    const canApprove = auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_ENHANCED_CONTENTS, 'approve');
    const canPublish = auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_ENHANCED_CONTENTS, 'publish');
    const canViewAll = auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_ENHANCED_CONTENTS, 'view_all');
    const isReviewer= canApprove && canPublish && !canViewAll;

    const onToggleApproval = (entity: EnhancedContentDataType) => {
        const currentApproved = entity.approved ?? true;
        entity.id && approvalEnhancedEntryMutation.mutate({
            id: entity.id,
            approve: !currentApproved,
        });
    };

    const enhancedContentGridColumns = columns({
        t,
        onEdit,
        onDelete,
        onToggleApproval,
        canApprove,
        isReviewer
    });
    const onDeleteEntriesAndCloseModal = (entities: EnhancedContentDataType[]) => {
        setIsDeleteConfirmationDialogOpen(false);
        setShowConfirmationModal(false);
        return entities.forEach(row => row.id && deleteEnhancedContentMutation.mutate({ id: row.id }));
    };

    return (
        <Panel>
            <PanelHeader
                icon={PencilSquareIcon}
                title={t('ENHANCED_CONTENT')}
                subTitle={t('ENHANCED_CONTENT.SUBTITLE')}
            />
            <PanelBody className="tw-h-[70vh]">
                <Grid
                    toolbar={() => (!isReviewer ?
                        <Toolbar
                            textFilter={textFilter}
                            onTextFilterChange={setTextFilter}
                            onNewContentClick={() => setIsDialogContentOpen(true)}
                            deleteEnabled={selectedRows.length > 0}
                            onDeleteClick={() => onDelete(selectedRows)}
                            onPublishClick={() => onPublish(selectedRows)}
                            entriesApproved={selectedRows.every(
                                (row) => row.approved ?? true,
                            )}
                            canPublish={canPublish}
                        />: <></>
                    )}
                    columns={enhancedContentGridColumns}
                    isLoading={isLoading}
                    isLoadingNextPage={isFetchingNextPage}
                    data={canViewAll || (!canApprove && !canPublish)? enhancedContentData ?? []: publishedItemsContentData}
                    onInfiniteScroll={onInfiniteScroll}
                    onSelectedRowsChange={onSelectedRowsChange}
                />

                {isContentDialogOpen &&
                <ContentDialog
                    open={true}
                    entity={selectedEntity}
                    onClose={() => {
                        setSelectedEntity(undefined);
                        refetchData();
                        setIsDialogContentOpen(false);
                    }}
                    canPublish={canPublish}
                    canApprove={canApprove}
                    isReviewer={isReviewer}
                />}

                { isDeleteConfirmationDialogOpen &&
                <ConfirmationModal
                    title={t('CONFIRM_DELETE')}
                    subTitle= {t('EDITOR.DELETE_CONFIRMATION_TEXT.ROW')}
                    onSubmit={()=>onDeleteEntriesAndCloseModal(selectedRows)}
                    onClose={() => setIsDeleteConfirmationDialogOpen(false)}
                    show={showConfirmationModal}
                />
                }
            </PanelBody>
        </Panel>
    );
}
